import { useState, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { submitBugReport, BugReportResponse } from '../../api/feedback'

interface BugReportModalProps {
  onClose: () => void
  onSuccess?: (result: BugReportResponse) => void
}

type Category = 'ui' | 'crash' | 'feature' | 'performance' | 'other'

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'ui', label: 'Интерфейс' },
  { value: 'crash', label: 'Ошибка/Краш' },
  { value: 'feature', label: 'Функция не работает' },
  { value: 'performance', label: 'Производительность' },
  { value: 'other', label: 'Другое' },
]

export default function BugReportModal({ onClose, onSuccess }: BugReportModalProps) {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<Category | ''>('')
  const [screenshots, setScreenshots] = useState<File[]>([])
  const [screenshotPreviews, setScreenshotPreviews] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const mutation = useMutation({
    mutationFn: () => submitBugReport(
      { description, category: category || undefined },
      screenshots.length > 0 ? screenshots : undefined
    ),
    onSuccess: (result) => {
      onSuccess?.(result)
      onClose()
    },
    onError: (err: Error) => {
      setError(err.message || 'Не удалось отправить отчёт')
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Validate and add files
    const validFiles: File[] = []
    const previews: string[] = []

    for (const file of files) {
      // Check type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Поддерживаются только JPG, PNG, WebP')
        continue
      }

      // Check size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Максимальный размер файла — 10 МБ')
        continue
      }

      // Check total count
      if (screenshots.length + validFiles.length >= 3) {
        setError('Максимум 3 скриншота')
        break
      }

      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      setError(null)

      // Create previews
      validFiles.forEach((file) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          previews.push(e.target?.result as string)
          if (previews.length === validFiles.length) {
            setScreenshotPreviews((prev) => [...prev, ...previews])
          }
        }
        reader.readAsDataURL(file)
      })

      setScreenshots((prev) => [...prev, ...validFiles])
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index))
    setScreenshotPreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    if (description.length < 10) {
      setError('Описание должно быть не менее 10 символов')
      return
    }
    setError(null)
    mutation.mutate()
  }

  const isValid = description.length >= 10

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fade-in max-h-[90vh] flex flex-col" data-testid="bug-report-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-800">
              Сообщить об ошибке
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Опишите проблему *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Что произошло? Как воспроизвести ошибку?"
              rows={4}
              maxLength={5000}
              data-testid="bug-description"
              className={clsx(
                'w-full px-4 py-3 rounded-xl border resize-none',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
                'placeholder:text-neutral-400',
                error && description.length < 10 ? 'border-red-300' : 'border-neutral-200'
              )}
            />
            <div className="mt-1 text-xs text-neutral-400 text-right">
              {description.length} / 5000
            </div>
          </div>

          {/* Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Категория
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              className={clsx(
                'w-full px-4 py-3 rounded-xl border border-neutral-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
                'bg-white'
              )}
            >
              <option value="">Выберите категорию</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Screenshots */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Скриншоты (до 3)
            </label>

            {/* Preview grid */}
            {screenshotPreviews.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {screenshotPreviews.map((preview, index) => (
                  <div key={index} className="relative w-20 h-20">
                    <img
                      src={preview}
                      alt={`Screenshot ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeScreenshot(index)}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add button */}
            {screenshots.length < 3 && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={clsx(
                    'w-full py-3 px-4 rounded-xl border-2 border-dashed border-neutral-200',
                    'text-neutral-500 hover:text-neutral-700 hover:border-neutral-300',
                    'flex items-center justify-center gap-2 transition-colors'
                  )}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Добавить скриншот
                </button>
              </>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Success message */}
          {mutation.isSuccess && (
            <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-xl text-sm">
              {mutation.data?.message || 'Отчёт отправлен!'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-100 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={mutation.isPending}
            className={clsx(
              'flex-1 py-2.5 px-4 rounded-xl font-medium',
              'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
              'transition-colors',
              mutation.isPending && 'opacity-50 cursor-not-allowed'
            )}
          >
            Отмена
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || mutation.isPending}
            data-testid="bug-submit-button"
            className={clsx(
              'flex-1 py-2.5 px-4 rounded-xl font-medium',
              'bg-primary-500 text-white hover:bg-primary-600',
              'transition-colors',
              (!isValid || mutation.isPending) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {mutation.isPending ? 'Отправка...' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  )
}
